import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormDialogConfig } from '@app/shared/models';
import { AuthorizationNumberService } from '@app/shared/services';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
    selector: 'ifa-form-dialog',
    templateUrl: './form-dialog.component.html',
    styleUrls: ['./form-dialog.component.scss'],
    imports: [
        CommonModule,
        DialogModule,
        ButtonModule,
        ReactiveFormsModule,
        SelectModule,
        FormsModule,
        InputTextModule,
        MultiSelectModule,
        ProgressSpinnerModule,
    ]
})
export class FormDialogComponent implements OnInit {
  contentText = '';
  submitLabel = '';
  formDialogConfigs: FormDialogConfig[] = [];
  formGroup: FormGroup;
  isInitializing: boolean = true;
  isLoading: boolean;
  authFound = true;
  previouslySearched: string;

  Validators = Validators;

  public ref: DynamicDialogRef = inject(DynamicDialogRef);
  public config: DynamicDialogConfig = inject(DynamicDialogConfig);
  public authService = inject(AuthorizationNumberService);

  private readonly destroyReference = inject(DestroyRef);

  get authorizationNumber(): AbstractControl {
    return this.formGroup.get('authorizationNumber');
  }

  // Note: at most we should only need 1 parameter for our callback.
  // If you need more parameters, pass in an object
  callback: (parameter?: string) => Promise<boolean>;

  public ngOnInit(): void {
    if (this.config.data) {
      this.config.closable = true;
      this.contentText = this.config.data.contextText;
      this.submitLabel = this.config.data.submitLabel;
      this.callback = this.config.data.callback || null;
      this.initializeFormControllers();
    }
  }

  public cancelClick(): void {
    this.ref.close();
    this.ref.destroy();
  }

  public async submitClick(): Promise<void> {
    if (this.callback !== null) {
      this.isLoading = true;
      try {
        this.authFound = await this.callback(this.authorizationNumber.value);
        this.previouslySearched = this.authorizationNumber.value;
      } finally {
        this.isLoading = false;
      }
    }

    if (this.authFound) {
      this.ref.close(this.formGroup.value);
      this.ref.destroy();
    }
  }

  // Note this is used in the HTML for live form validation
  public isInvalid(formControlName: string): boolean {
    return this.formGroup.get(formControlName).touched && this.formGroup.get(formControlName).invalid;
  }

  private initializeFormControllers(): void {
    let formControls: Record<string, FormControl> = {};

    this.config.data.formDialogConfigs
      ?.pipe(takeUntilDestroyed(this.destroyReference))
      .subscribe((formDialogConfigs: Array<FormDialogConfig>): void => {
        this.formDialogConfigs = formDialogConfigs;
        this.formDialogConfigs?.forEach((x) => {
          if (x.formControlName) {
            formControls[x.formControlName] =
              x.validators?.length > 0 ? new FormControl(null, x.validators) : new FormControl(null);
          }
        });

        this.formGroup = new FormGroup(formControls);
        this.isInitializing = false;
      });
  }
}
